import React from 'react';
import { useTranslation } from 'react-i18next';
import SelectionPanel, {
  NavSection,
} from 'shared/components/configPanel/SelectionPanel';
import { routeRecordId } from 'routes/routeDisplayName';
import { SourceFilter } from 'routes/sourceFilters/sourceFilterTypes';
import { Rule } from 'routes/transformationRules/fieldMappingTypes';
import { AccountBundle, FlowDirectionKey, Selection } from '../flowMapTypes';
import { FlowArrow } from '../useFlowGraph';
import { arrowOffersRules } from '../arrowRules';
import CopyIdButton from './CopyIdButton';
import PanelSkeleton from './PanelSkeleton';
import FlowSendingToggle from './FlowSendingToggle';
import { DeviceFilterSection, FieldMappingSection } from './RuleSections';
import { Confirmation } from './ConfirmationModal';
import Alert from 'shared/components/ui/Alert';

type FlowSelection = Extract<Selection, { kind: 'flow' }>;

export type FlowPanelProps = {
  selection: FlowSelection;
  select: (next: Selection) => void;
  route: any;
  providerId: string;
  destinations: any[];
  bundles: Record<string, AccountBundle>;
  arrows: FlowArrow[];
  routeEnabled: boolean;
  confirm: (confirmation: Confirmation) => void;
  reverseRoutesBySite?: Record<string, string[]>;
  reverseRuleEditable?: (destId: string) => boolean;
  providerAlsoProvidesFor?: number | null;
  getRule: (destId: string) => Rule | null;
  getReverseRule?: (destId: string) => Rule | null;
  getSourceFilter?: (
    destId: string,
    direction: FlowDirectionKey,
  ) => SourceFilter | null | undefined;
  onOpenRules: (
    dest: any,
    direction: FlowDirectionKey,
    kind: 'device' | 'field_mapping',
  ) => void;
  onToggleRouteEnabled?: (enabled: boolean) => void;
  onEnableReverseFlow: (dest: any) => void;
  onDisableReverseFlow: (dest: any) => void;
};

// The panel for one arrow on the map: the flow's on/off switch and its rules.
const FlowPanel = ({
  selection,
  select,
  route,
  providerId,
  destinations,
  bundles,
  arrows,
  routeEnabled,
  confirm,
  ...props
}: FlowPanelProps) => {
  const { t } = useTranslation();
  const arrow = arrows.find(
    (a) => a.id === `${selection.destinationId}-${selection.direction}`,
  );
  const providerBundle = bundles[providerId];
  const destBundle = bundles[selection.destinationId];
  const dest = destinations.find((d: any) => d.id === selection.destinationId);
  if (!arrow || !providerBundle || !destBundle) return <PanelSkeleton />;

  const sender = arrow.direction === 'out' ? providerBundle : destBundle;
  const receiver = arrow.direction === 'out' ? destBundle : providerBundle;
  // Pull/push settings are configured on the accounts that own them; the
  // arrow carries what belongs to the flow itself — its on/off state and its
  // field-mapping rules. Both directions have rules, but the incoming one
  // keeps them on the reverse Route's own record: until that Route exists
  // (the direction is off) there is nothing to attach a rule to.
  const nav: NavSection[] = arrowOffersRules({
    direction: arrow.direction,
    state: arrow.state,
    reverseRecordExists: !!props.reverseRuleEditable?.(selection.destinationId),
  })
    ? [
        {
          cat: t('Rules'),
          // One entry per rule kind, each with its own card and action — a
          // future geographic or time rule is one more entry here.
          items: [
            { slug: 'field-mapping', label: t('Field mappings') },
            // Source filters are forward-only: the reverse arrow never offers them.
            ...(arrow.direction === 'out'
              ? [{ slug: 'device-filters', label: t('Source filters') }]
              : []),
          ],
        },
      ]
    : [];

  // A section from the URL only counts if this arrow offers it: a stale or
  // pasted link must not open source filters on the reverse direction.
  const offeredSlugs = nav.flatMap((group) =>
    group.items.map((item) => item.slug),
  );
  const activeSlug =
    selection.section && offeredSlugs.includes(selection.section)
      ? selection.section
      : nav[0]?.items[0]?.slug;

  // An arrow that can't be enabled has no switch at all.
  const hasToggle =
    arrow.direction === 'out' || arrow.state !== 'not-enableable';

  const note =
    arrow.direction === 'in' && arrow.state === 'disabled'
      ? t(
          'Not enabled yet. Configure both accounts on the map, then turn this direction on.',
        )
      : undefined;

  const arrowRouteId: string | undefined =
    arrow.direction === 'out'
      ? routeRecordId(route)
      : (props.reverseRoutesBySite?.[selection.destinationId] || [])[0];

  return (
    <SelectionPanel
      title={
        <span className="flex min-w-0 flex-col gap-1">
          <span className="truncate">{`${sender.name} → ${receiver.name}`}</span>
          {/* The record this arrow stands for: the Route itself going
              out, the separate reverse Route coming back (none until
              that direction has been enabled). */}
          {arrowRouteId && (
            <span className="flex items-center text-body-sm font-normal">
              <CopyIdButton
                key={arrowRouteId}
                id={arrowRouteId}
                testId="ConfigurationsTab-copy-route-id-button"
                label={t('Copy Route ID')}
              />
            </span>
          )}
        </span>
      }
      subtitle={
        note ? (
          <span className="text-body-sm text-ui-text-primary-text">{note}</span>
        ) : undefined
      }
      headerActions={
        hasToggle ? (
          <FlowSendingToggle
            arrow={arrow}
            routeEnabled={routeEnabled}
            providerBundle={providerBundle}
            destBundle={destBundle}
            dest={dest}
            providerAlsoProvidesFor={props.providerAlsoProvidesFor}
            onToggleRouteEnabled={props.onToggleRouteEnabled}
            onEnableReverseFlow={props.onEnableReverseFlow}
            onDisableReverseFlow={props.onDisableReverseFlow}
            confirm={confirm}
          />
        ) : undefined
      }
      banner={
        arrow.state === 'not-enableable' && arrow.reason ? (
          // A warning like SharedAccountNotice, not a neutral note.
          <Alert status="warning" className="mb-5">
            {arrow.reason}
          </Alert>
        ) : undefined
      }
      nav={nav}
      activeSlug={activeSlug}
      onSelectSection={(slug) =>
        select({
          kind: 'flow',
          destinationId: selection.destinationId,
          direction: selection.direction,
          section: slug,
        })
      }
      emptyNavNote={t('Nothing to configure for this direction.')}
    >
      {activeSlug === 'field-mapping' ? (
        <FieldMappingSection
          rule={
            dest
              ? arrow.direction === 'in'
                ? (props.getReverseRule?.(dest.id) ?? null)
                : props.getRule(dest.id)
              : null
          }
          direction={arrow.direction}
          onOpen={() =>
            props.onOpenRules(dest, arrow.direction, 'field_mapping')
          }
        />
      ) : activeSlug === 'device-filters' ? (
        <DeviceFilterSection
          sourceFilter={
            dest
              ? (props.getSourceFilter?.(dest.id, arrow.direction) ?? null)
              : null
          }
          destinationName={dest?.name ?? ''}
          onOpen={() => props.onOpenRules(dest, arrow.direction, 'device')}
        />
      ) : (
        <p className="text-body-sm text-ui-text-primary-text">
          {t(
            'Pull and push settings are configured on the accounts — click a node on the map.',
          )}
        </p>
      )}
    </SelectionPanel>
  );
};

export default FlowPanel;
